/**
 * PositionPrimitive.ts — TradingView-style position overlay.
 *
 * Малює на canvas:
 *  - Зелена зона (entry → TP) — напрямок прибутку
 *  - Червона зона (entry → SL) — напрямок збитку
 *  - Горизонтальні лінії на entry / TP / SL
 *  - Лейбли з ціною, % та PnL
 */

import type { ISeriesPrimitive, IPrimitivePaneView, IPrimitivePaneRenderer, Time } from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

// ─── Публічний інтерфейс даних ────────────────────────────────────────────────

export interface PositionData {
    entry:          number;
    takeProfit?:    number;
    stopLoss?:      number;
    side:           'LONG' | 'SHORT';
    leverage?:      number;
    unrealizedPnl?: number;
}

// ─── Утиліти ─────────────────────────────────────────────────────────────────

function fmtPrice(p: number): string {
    if (p >= 1000) return p.toFixed(2);
    if (p >= 1)    return p.toFixed(4);
    return p.toFixed(6);
}

function fmtPct(entry: number, target: number, side: 'LONG' | 'SHORT'): string {
    const raw = side === 'LONG'
        ? (target - entry) / entry * 100
        : (entry - target) / entry * 100;
    return (raw >= 0 ? '+' : '') + raw.toFixed(2) + '%';
}

// ─── Рендерер зон (background, нижній шар) ───────────────────────────────────

class BandRenderer implements IPrimitivePaneRenderer {
    constructor(
        private _data: PositionData,
        private _series: any,
        private _vpr: number,
    ) {}

    draw(target: CanvasRenderingTarget2D): void {
        target.useBitmapCoordinateSpace(({ context: ctx, verticalPixelRatio: vpr }) => {
            const W = ctx.canvas.width;

            const entryCoord = this._series.priceToCoordinate(this._data.entry);
            if (entryCoord === null) return;
            const entryY = Math.round(entryCoord * vpr);

            // TP зона (зелена)
            if (this._data.takeProfit != null) {
                const tpCoord = this._series.priceToCoordinate(this._data.takeProfit);
                if (tpCoord !== null) {
                    const tpY = Math.round(tpCoord * vpr);
                    ctx.fillStyle = 'rgba(38, 166, 154, 0.13)';
                    ctx.fillRect(0, Math.min(entryY, tpY), W, Math.abs(tpY - entryY));
                }
            }

            // SL зона (червона)
            if (this._data.stopLoss != null) {
                const slCoord = this._series.priceToCoordinate(this._data.stopLoss);
                if (slCoord !== null) {
                    const slY = Math.round(slCoord * vpr);
                    ctx.fillStyle = 'rgba(239, 83, 80, 0.10)';
                    ctx.fillRect(0, Math.min(entryY, slY), W, Math.abs(slY - entryY));
                }
            }
        });
    }
}

// ─── Рендерер ліній та лейблів (верхній шар) ─────────────────────────────────

class LinesRenderer implements IPrimitivePaneRenderer {
    constructor(
        private _data: PositionData,
        private _series: any,
    ) {}

    draw(target: CanvasRenderingTarget2D): void {
        target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
            const W = ctx.canvas.width;

            const entryCoord = this._series.priceToCoordinate(this._data.entry);
            if (entryCoord === null) return;
            const entryY = Math.round(entryCoord * vpr);

            const GREEN  = '#26a69a';
            const RED    = '#ef5350';
            const ORANGE = '#FF9800';

            // ── TP лінія + лейбл ──────────────────────────────────────────────
            if (this._data.takeProfit != null) {
                const tpCoord = this._series.priceToCoordinate(this._data.takeProfit);
                if (tpCoord !== null) {
                    const tpY = Math.round(tpCoord * vpr);
                    this._line(ctx, W, tpY, hpr, GREEN, []);
                    const pct = fmtPct(this._data.entry, this._data.takeProfit, this._data.side);
                    this._label(ctx, W, tpY, hpr,
                        `TP  ${fmtPrice(this._data.takeProfit)}  ${pct}`,
                        GREEN, 'rgba(38,166,154,0.92)', '#fff');
                }
            }

            // ── SL лінія + лейбл ──────────────────────────────────────────────
            if (this._data.stopLoss != null) {
                const slCoord = this._series.priceToCoordinate(this._data.stopLoss);
                if (slCoord !== null) {
                    const slY = Math.round(slCoord * vpr);
                    this._line(ctx, W, slY, hpr, RED, [5 * hpr, 4 * hpr]);
                    const pct = fmtPct(this._data.entry, this._data.stopLoss, this._data.side);
                    this._label(ctx, W, slY, hpr,
                        `SL  ${fmtPrice(this._data.stopLoss)}  ${pct}`,
                        RED, 'rgba(239,83,80,0.92)', '#fff');
                }
            }

            // ── Entry лінія ───────────────────────────────────────────────────
            this._line(ctx, W, entryY, hpr, ORANGE, [8 * hpr, 4 * hpr]);

            // ── Entry лейбл ──────────────────────────────────────────────────
            const sideStr = this._data.side === 'LONG' ? 'Long' : 'Short';
            const levStr  = this._data.leverage ? `${this._data.leverage}x` : '';
            const pnl     = this._data.unrealizedPnl ?? 0;
            const pnlStr  = pnl >= 0
                ? `+$${pnl.toFixed(2)}`
                : `-$${Math.abs(pnl).toFixed(2)}`;
            const pnlColor = pnl >= 0 ? GREEN : RED;

            this._entryLabel(ctx, W, entryY, hpr, sideStr, levStr, fmtPrice(this._data.entry), pnlStr, pnlColor);
        });
    }

    // ── Горизонтальна лінія ───────────────────────────────────────────────────
    private _line(ctx: CanvasRenderingContext2D, w: number, y: number, hpr: number, color: string, dash: number[]) {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth   = 1.5 * hpr;
        ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
        ctx.restore();
    }

    // ── Кольоровий лейбл ──────────────────────────────────────────────────────
    private _label(
        ctx: CanvasRenderingContext2D,
        canvasW: number, y: number, hpr: number,
        text: string, borderColor: string, bgColor: string, textColor: string,
    ) {
        const fs   = 10.5 * hpr;
        const padX = 9 * hpr;
        const padY = 4.5 * hpr;
        ctx.save();
        ctx.font = `600 ${fs}px -apple-system, "Segoe UI", Arial, sans-serif`;
        const tw   = ctx.measureText(text).width;
        const boxW = tw + padX * 2;
        const boxH = fs + padY * 2;
        const boxX = canvasW - boxW - 4 * hpr;
        const boxY = y - boxH / 2;

        ctx.fillStyle = bgColor;
        this._rrect(ctx, boxX, boxY, boxW, boxH, 3 * hpr);
        ctx.fill();

        ctx.fillStyle = textColor;
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, boxX + padX, y);
        ctx.restore();
    }

    // ── Entry лейбл з кольоровим PnL та лівим бордером ───────────────────────
    private _entryLabel(
        ctx: CanvasRenderingContext2D,
        canvasW: number, y: number, hpr: number,
        side: string, leverage: string, price: string, pnl: string, pnlColor: string,
    ) {
        const fs     = 10.5 * hpr;
        const padX   = 9 * hpr;
        const padY   = 4.5 * hpr;
        const borderW = 3 * hpr;

        ctx.save();
        ctx.font = `600 ${fs}px -apple-system, "Segoe UI", Arial, sans-serif`;

        const mainText = `${side}  ${leverage}  ${price}  `;
        const mainW    = ctx.measureText(mainText).width;
        const pnlW     = ctx.measureText(pnl).width;
        const boxW     = mainW + pnlW + padX * 2 + borderW;
        const boxH     = fs + padY * 2;
        const boxX     = canvasW - boxW - 4 * hpr;
        const boxY     = y - boxH / 2;

        // Фон
        ctx.fillStyle = 'rgba(20,20,20,0.90)';
        this._rrect(ctx, boxX, boxY, boxW, boxH, 3 * hpr);
        ctx.fill();

        // Лівий помаранчевий бордер
        ctx.fillStyle = '#FF9800';
        ctx.fillRect(boxX, boxY, borderW, boxH);

        // Основний текст
        ctx.fillStyle    = '#ffffff';
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(mainText, boxX + padX + borderW, y);

        // PnL кольором
        ctx.fillStyle = pnlColor;
        ctx.fillText(pnl, boxX + padX + borderW + mainW, y);

        ctx.restore();
    }

    private _rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
        ctx.beginPath();
        if ((ctx as any).roundRect) {
            (ctx as any).roundRect(x, y, w, h, r);
        } else {
            ctx.rect(x, y, w, h);
        }
    }
}

// ─── View-обгортки ────────────────────────────────────────────────────────────

class BandView implements IPrimitivePaneView {
    constructor(private _r: BandRenderer) {}
    renderer() { return this._r; }
    zOrder()   { return 'bottom' as const; }
}

class LinesView implements IPrimitivePaneView {
    constructor(private _r: LinesRenderer) {}
    renderer() { return this._r; }
    zOrder()   { return 'normal' as const; }
}

// ─── Публічний примітив ───────────────────────────────────────────────────────

export class PositionPrimitive implements ISeriesPrimitive<Time> {
    private _data:   PositionData;
    private _series: any = null;
    private _views:  IPrimitivePaneView[] = [];

    constructor(data: PositionData) {
        this._data = data;
    }

    attached({ series }: any): void {
        this._series = series;
        this._rebuild();
    }

    detached(): void {
        this._series = null;
        this._views  = [];
    }

    /** Відчіпити примітив від серії */
    detach(): void {
        this._series?.detachPrimitive(this);
    }

    updateAllViews(): void {
        this._rebuild();
    }

    paneViews(): readonly IPrimitivePaneView[] {
        return this._views;
    }

    private _rebuild(): void {
        if (!this._series) { this._views = []; return; }
        this._views = [
            new BandView(new BandRenderer(this._data, this._series, 1)),
            new LinesView(new LinesRenderer(this._data, this._series)),
        ];
    }
}
